import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import LoaderContainer from '../../shared/loaderContainer/LoaderContainer';
import { ITableHeader } from '../../shared/tables/models/ITableHeader';
import TemplateTable from '../../shared/tables/TemplateTable';
import { TableContainer } from '../../Organizations/OrganizationsList';
import { HTTP_STATUS } from '../../../http/constants/http.status';
import { AppConstants } from '../../../constants/AppConstants';
import { useTheme } from '../../../context/themeContext';
import { setURLPaths } from '../../../app/organizationReducer';
import { useNavigate, useParams } from 'react-router-dom';
import { Clipboard, customAddCircle } from '../../../assets/components/svgs';
import { setToastData } from '../../../app/toastReducer';
import {
  capitalize,
  cloneDeep,
  compact,
  filter,
  find,
  findIndex,
  isEmpty,
  isEqual,
  isUndefined,
  map
} from 'lodash';
import { getFailureMessage } from '../../../util/ErrorUtil';
import { RouteConstants } from '../../../constants/RouteConstants';
import { EllipsisDiv } from '../../../styles/globalStyles';
import AddCircle from '../../assets/addCircle.svg';
import {
  deleteCareplanActivity,
  getCarePlan,
  getCarePlanActivities
} from '../../../http/careplan-services';
import { useDidMountEffect } from '../../../util/customHooks';
import Card from '../../shared/cards/Card';
import styled from 'styled-components';
import { setTableOptions } from '../../../app/templateTableReducer';
import BiButton from '../../primitives/buttons/BiButton.primitive';
import MenuButtonsPortal from '../../Menu/MenuButtonsPortal';
import {
  configureCareplanCategories,
  fetchCustomRoleUserList,
  filterAuthorData,
  filterOrgNames,
  getCareplanListData
} from '../carePlanUtils';
import {
  setActivityData,
  setCareplanConfig,
  setCareplanData,
  setListData
} from '../../../app/carePlanReducer';
import moment from 'moment';
import { MenuOptions } from '../../primitives/dotMenu/ContextMenu';
import { MenuConstants } from '../../../constants/MenuConstants';
import Modal from '../../shared/ModalPortal/Modal';
import { ExclamationIcon } from '@heroicons/react/outline';
import { checkForPermission } from '../../../util/permissionUtils';

/**
 * @parentId careplan
 * @manager Careplan Activities Listing
 * @overview Lists Activities under a Careplan
 *   <section>
 *       <p>
 *          Activities listing page list all the Activities under a Careplan. User can navigate to Activities listing page by clicking <b>Care Plan Template</b> navigation menuitem under the <strong>Platform Core</strong> menu group of admin portal left sidebar and then clicking on the dot menu option of the careplan table rowitem and selecting <strong>View Careplan Activities</strong>.
 *          The Default pagination size be fixed to 20 items, which can be changed to 50 or 100 from the dropdown menu of datagrid footer (The paging options are defined from the configuration). Datagrid footer will also display the current page and total number of pages available based on the choosen page size & Activities data.
 *       </p>
 *       <br>
 *       <p>
 *          Activities can be filtered based on Status, Kind of Activity, To be performed, Performer Type and Performer. Admin user(s) can create new Activity  by clicking on <b>Add an Activity</b> button provided on the admin portal toolbar,
 *           which will navigate user to Activity creation page. <b>Add an Activity</b> button will be shown based on logged in user role API permissions as described below.
 *       </p>
 *       <br>
 *       <p>
 *           Datagrid will display details of each activitie as row item with each column representing below object property.
 *           <br>
 *           <ul>
 *               <li>Status</li>
 *               <li>Kind of Activities</li>
 *               <li>Performer</li>
 *               <li>To be performed</li>
 *               <li>Start date/time</li>
 *               <li>End date/time</li>
 *               <li>Last Modified On</li>
 *           </ul>
 *       </p>
 *     </section>
 *        <section>
 *         <p>
 *          <h4>Supported Filters</h4>
 *          Careplan Activities listing table will have the following filter options:
 *          <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Type</th>
 *               <th>Description</th>
 *            </tr>
 *              <tr>
 *               <td>Status</td>
 *               <td>Dropdown</td>
 *               <td>Used to filter based on the Status Field</td>
 *            </tr>
 *             <tr>
 *               <td>Kind of Activity</td>
 *                <td>Dropdown</td>
 *               <td>Used to filter based on kind of activity</td>
 *            </tr>
 *             <tr>
 *               <td>To be performed</td>
 *               <td>Radio Button</td>
 *               <td>Toggle to filter based on To be performed</td>
 *            </tr>
 *               <tr>
 *               <td>Performer Type</td>
 *               <td>Dropdown</td>
 *               <td>A Dependent based Dropdown which on selection will populate data in Performer Dropdown</td>
 *            </tr>
 *              <tr>
 *               <td>Performer</td>
 *               <td>Dropdown</td>
 *               <td>Used to filter based on Performer</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 *      </section>
 * <section>
 *         <p>
 *          <h4>Table RowItem Dot Menu Options</h4>
 *         Activities listing table will have the following options in individual row items:
 *          <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Description</th>
 *               <th>Permission Needed</th>
 *            </tr>
 *             <tr>
 *               <td>Delete</td>
 *               <td>Deletes the Note. On selection a confirmation popup appears.</td>
 *               <td>careplan-service.careplan.activities.delete</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 * </section>
 *       <section>
 *         <p>
 *          <h4>Configuration based options</h4>
 *          Careplan Activities dropdown options in filters are configured via the System Setting with category "careplan.template.default.values". User can configure these options in the system settings for the following fields in listing:
 *        <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Key</th>
 *            </tr>
 *             <tr>
 *               <td>Status</td>
 *               <td>careplan.template.activities.status.values</td>
 *            </tr>
 *             <tr>
 *               <td>Kind of Activity</td>
 *               <td>careplan.template.activities.kind.types</td>
 *            </tr>
 *              <tr>
 *               <td>Performer Type</td>
 *               <td>careplan.template.activities.performer.types</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 *      </section>
 *     <section>
 *     <section>
 *      <h4>Failure Status Codes</h3>
 *       <p>
 *       This section describes the List Careplan Activities Status Code information.
 *       <br>
 *       <table>
 *           <tr>
 *               <th>HTTP Status Code</th>
 *               <th>Service Error Code</th>
 *               <th>Error Message</th>
 *           </tr>
 *           <tr>
 *               <td>403</td>
 *               <td>CPS_PERMISSION_DENIED</td>
 *               <td>You do not have permission to view this page</td>
 *           </tr>
 *           <tr>
 *               <td>500</td>
 *               <td>CPS_INTERNAL_ERROR</td>
 *               <td>Internal Server Error</td>
 *           </tr>
 *           <tr>
 *               <td>503</td>
 *               <td></td>
 *               <td>Service Unavailable</td>
 *           </tr>
 *       </table>
 *   </p>
 *   </section
 *   <section>
 *      <h4>Dependent System settings, Platform services & Role Permission</h3>
 *      <p>This section describes the list of dependent system settings & platform services required for functioning of Activities listing page.</p>
 *       <h5>System Settings</h4>
 *       <p>Table lists all the dependent system setting(s) defined in configuration service with either global/organization scope</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Key</th>
 *               <th>Type</th>
 *               <th>Value</th>
 *               <th>Scope</th>
 *           </tr>
 *           <tr>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h5>Platform Service(s)</h4>
 *      <p>Table lists all the dependent platform service(s) with specific version(s) for careplan listing</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Service Name</th>
 *               <th>Version</th>
 *           </tr>
 *           <tr>
 *               <td>Careplan Service</td>
 *               <td>1.3.0</td>
 *           </tr>
 *           <tr>
 *               <td>User Management Service</td>
 *               <td>1.6.0</td>
 *           </tr>
 *           <tr>
 *               <td>Configuration Service</td>
 *               <td>1.6.0</td>
 *         </tr>
 *       </table>
 *       <br>
 *      <h5>API Role Permission(s)</h4>
 *      <p>Table lists the required API role permissions for careplan listing page</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>API URL</th>
 *               <th>API Method</th>
 *               <th>API Permission</th>
 *               <th>Required</th>
 *           </tr>
 *           <tr>
 *               <td>/careplans</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.list</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>/careplans/:id</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.get</td>
 *               <td>Yes</td>
 *           </tr>
 *             <tr>
 *               <td>/careplans/:id/activities</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.activity.list</td>
 *               <td>Yes</td>
 *            </tr>
 *            <tr>
 *               <td>/careplans/:id/activities/:id</td>
 *               <td>DELETE</td>
 *               <td>careplan-service.careplan.activitie.delete</td>
 *               <td>No</td>
 *            </tr>
 *       </table>
 *   </section>
 *   <section>
 *   </section>
 */

const CarePlanActivities = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [rows, setRows] = useState<any>([]);
  const [paging, setPaging] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentActivePage, setCurrentActivePage] = useState(0);
  const [performerList, setPerformerList] = useState<any>([]);

  const [deletePopup, setDeletePopup] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string>('');

  const globalFilters: any = useAppSelector((state) => state.templateTableReducer.tableOptions);
  const careplanData = useAppSelector((state) => state.carePlanData.careplanData);
  const roleAPIPermissions: any =
    useAppSelector((state) => state.initialLoadData.apiPermissions) || {};
  const listData: any = useAppSelector((state) => state.carePlanData.listData);
  const careplanConfig: any = useAppSelector((state) => state.carePlanData.careplanConfig) || {};

  const tableKey: string = 'careplan-activity-list';

  const listFilters = [
    {
      id: 'activityStatus',
      label: 'T_STATUS',
      component: 'picklist',
      type: 'single',
      itemsList: careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_STATUS_VALUES],
      selectedFilter: [],
      fieldKey: 'key',
      fieldValues: ['display'],
      disabled: false,
      hasMore: false,
      paging: { size: 1000, page: 1 },
      searchable: false
    },
    {
      id: 'activityKind',
      label: 'T_CARE_PLAN_ACTIVITY_ACTIVITY_KIND_FILTER',
      component: 'picklist',
      type: 'single',
      itemsList: careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_KIND_TYPES],
      selectedFilter: [],
      fieldKey: 'key',
      fieldValues: ['display'],
      disabled: false,
      hasMore: false,
      paging: { size: 1000, page: 1 },
      searchable: false
    },
    {
      id: 'doNotPerform',
      type: 'boolean',
      component: 'boolean',
      label: 'T_CARE_PLAN_ACTIVITY_PERFORMED_CHECK',
      placeholder: '',
      key: 'doNotPerform',
      isDependentFilter: false,
      disabled: false,
      searchable: false,
      value: false
    },
    {
      id: 'performerType',
      label: 'T_CARE_PLAN_ACTIVITY_PERFORMER_TYPE_FILTER',
      component: 'picklist',
      type: 'single',
      itemsList: careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_PERFORMER_TYPES],
      selectedFilter: [],
      fieldKey: 'key',
      fieldValues: ['display'],
      isDependentFilter: true,
      dependentChildId: 'performerId',
      disabled: false,
      hasMore: false,
      paging: { size: 25, page: 1 },
      searchable: false
    },
    {
      id: 'performerId',
      label: 'T_CARE_PLAN_ACTIVITY_PERFORMER_FILTER',
      component: 'picklist',
      type: 'single',
      itemsList: [],
      selectedFilter: [],
      fieldKey: 'id',
      fieldValues: ['firstName', 'lastName'],
      disabled: false,
      hasMore: false,
      paging: { size: 1000, page: 1 },
      searchable: true,
      displayDelimiter: ' '
    },
    {}
  ];

  const checkConfiguration = async () => {
    let config = await configureCareplanCategories();
    if (!isEmpty(config)) {
      dispatch(setCareplanConfig(config));
    }
  };

  useEffect(() => {
    if (isEmpty(careplanConfig)) {
      checkConfiguration();
    } else {
      setFilterList(activitiesFiltersList);
    }
  }, [careplanConfig]);

  const searchPerformerData = (data: any) => {
    setLoadNextData((prevState: any) => ({
      ...prevState,
      searchPerformerName: data,
      searchPerformer: true
    }));
  };

  const searchFuncMapping: any = {
    performerId: searchPerformerData
  };

  const fetchDependentFields = async (selectedItem: any) => {
    if (selectedItem.length) {
      let index = findIndex(filterList, function (item: any) {
        return item?.id === 'performerId';
      });

      const [item] = selectedItem;
      const { apiConfig, key } = item || {};
      const { fieldKey, fieldValues } = apiConfig || {};
      const listDataClone: any = cloneDeep(listData);

      if (index !== -1) {
        let element = cloneDeep(filterList[index]);
        element.itemsList = [];
        element.fieldValues = fieldValues;
        element.fieldKey = fieldKey;

        setFilterList((prevState: any) =>
          prevState.map((item: any) => (item.id === element.id ? { ...item, ...element } : item))
        );

        if (key && !listDataClone?.[key]?.length) {
          const data = await getCareplanListData(apiConfig);
          if (data.length) {
            listDataClone[key] = data;
          }
        }

        element.itemsList = listDataClone?.[key];

        setFilterList((prevState: any) =>
          prevState.map((item: any) => (item.id === element.id ? { ...item, ...element } : item))
        );

        if (!isEqual(listData, listDataClone)) {
          dispatch(setListData(listDataClone));
        }
        setLoadNextData((prevState: any) => ({
          ...prevState,
          searchPerformerType: key,
          fieldValues
        }));
      }
    } else {
      let index = findIndex(filterList, function (item: any) {
        return item?.id === 'performerId';
      });
      if (index !== -1) {
        let element = cloneDeep(filterList[index]);
        element.itemsList = [];
        element.fieldValues = 'display';

        setFilterList((prevState: any) =>
          prevState.map((item: any) => (item.id === element.id ? { ...item, ...element } : item))
        );

        setLoadNextData((prevState: any) => ({
          ...prevState,
          searchPerformerType: '',
          fieldValues: ''
        }));
      }
    }
  };

  const activitiesFiltersList = listFilters.map((filter: any) => {
    if (filter.id === 'performerType') {
      return {
        ...filter,
        fetchDependentFields: fetchDependentFields,
        isDependentFilter: true,
        searchData: searchFuncMapping[filter.id]
      };
    } else {
      return {
        ...filter,
        searchData: searchFuncMapping[filter.id]
      };
    }
  });

  const [filterList, setFilterList] = useState<Array<any>>(activitiesFiltersList);
  const [loadNextData, setLoadNextData] = useState<any>({
    searchPerformerName: '',
    searchPerformer: false,
    searchPerformerType: '',
    fieldValues: ''
  });

  const [filterPillsData, setFilterPillsData] = useState<Array<any>>([]);

  const getPerformerSearchConfig = (type: string) => {
    let element = find(
      careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_PERFORMER_TYPES],
      {
        key: type
      }
    );
    if (element) {
      let key = element?.apiConfig?.fieldValues;
      return { data: listData?.[type], searchKey: key || [] };
    } else {
      return { data: [], searchKey: [] };
    }
  };

  const updateFilterSetData = (filteredResponse: any, type: string) => {
    let initialFilterList: any = [...filterList];

    const currentFilterList = initialFilterList.map((obj: any) => {
      if (obj.id === type) {
        return {
          ...obj,
          itemsList: filteredResponse
        };
      } else {
        return { ...obj };
      }
    });
    setFilterList(currentFilterList);
  };

  const loadPerformerSearchData = () => {
    const { data, searchKey } = getPerformerSearchConfig(loadNextData.searchPerformerType);
    const filteredResponse = filterAuthorData(data, loadNextData.searchPerformerName, searchKey);
    updateFilterSetData(filteredResponse, 'performerId');
  };

  useDidMountEffect(async () => {
    if (loadNextData.searchPerformer) {
      loadPerformerSearchData();
    }
  }, [loadNextData]);

  const setBreadcrumps = () => {
    dispatch(
      setURLPaths([
        {
          key: RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES.replace(':id', params?.id as string),
          label: `${t('T_CARE_PLAN_TEMPLATES')} > ${careplanData?.title} > ${t(
            'T_CARE_PLAN_VIEW_ACTIVITIES'
          )}`
        }
      ])
    );
  };

  useEffect(() => {
    if (!isEmpty(careplanData)) {
      setBreadcrumps();
    } else {
      setIsLoading(true);
      getCareplanData();
    }
  }, [careplanData]);

  const getCareplanData = async () => {
    async function init() {
      try {
        let response = await getCarePlan(params?.id);
        const { status, data } = response;
        if (HTTP_STATUS.isSuccess(response?.status)) {
          dispatch(setCareplanData(data));
        } else {
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
          setIsLoading(false);
          navigate(-1);
        }
      } catch (e) {
        dispatch(
          setToastData({
            isToastActive: true,
            toastMessage: t('T_ERROR_SOMETHING_WRONG'),
            toastType: 'error'
          })
        );
        setIsLoading(false);
        navigate(-1);
      }
    }
    if (
      (params?.id && !isEmpty(careplanData) && params.id !== careplanData?.id) ||
      (isEmpty(careplanData) && params?.id)
    ) {
      await init();
    }
  };

  useEffect(() => {
    if (!params?.id) {
      dispatch(
        setToastData({
          toastMessage: t('T_ERROR_SOMETHING_WRONG'),
          isToastActive: true,
          toastType: 'error'
        })
      );
      navigate(-1);
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(globalFilters[tableKey])) {
      const { tableFilters, searchText, currentPage, size, sortName, sortType } =
        globalFilters[tableKey];
      applyFilters(searchText, currentPage, size, sortName, sortType, tableFilters);
    } else {
      dispatch(
        setTableOptions({
          [tableKey]: {
            ...globalFilters[tableKey],
            searchText: '',
            currentPage: AppConstants.DEFAULT_PAGE,
            size: AppConstants.DEFAULT_PAGE_SIZE,
            sortName: AppConstants.DEFAULT_SORT_BY,
            sortType: AppConstants.DEFAULT_SORT_DESC,
            tableFilters: {}
          }
        })
      );
    }
  }, [globalFilters]);

  const loadCarePlanActivities = async (
    page: number,
    size: number,
    sort: string = AppConstants.DEFAULT_SORT_BY,
    sortType: string = AppConstants.DEFAULT_SORT_DESC,
    filters?: any
  ) => {
    if (params?.id) {
      const response = await getCarePlanActivities(
        params.id,
        page,
        size,
        sortType ? [`${sort},${sortType}`] : [],
        filters
      );
      const { status, data: careTemplateResponse } = response;
      if (status === HTTP_STATUS.HTTP_OK) {
        const { data: activities = [], paging: pagingData } = careTemplateResponse;
        if (activities.length) {
          setRows([...activities]);
        } else {
          setRows(`${t('T_NO_DATA_AVAILABLE_MSG')}`);
        }
        setPaging(pagingData);
      } else {
        dispatch(
          setToastData({
            toastMessage: getFailureMessage(response),
            isToastActive: true,
            toastType: 'error'
          })
        );
      }
    }
    setIsLoading(false);
  };

  const getMenuOptions = (data: any): Array<any> => {
    let optionList: any = [];
    if (checkForPermission([AppConstants.PERMISSION_CAREPLAN_TEMPLATE_ACTIVITY_DELETE])) {
      optionList.push({
        display: `${t('T_DELETE')}`,
        key: MenuConstants.MENU_CARE_PLAN_ACTIVITIES_DELETE,
        action: () => {
          setDeletePopup(true);
          setDeleteId(data?.id);
        }
      });
    }

    return optionList;
  };

  const tableHeadersBuilder = () => {
    let TABLE_HEADERS: Array<ITableHeader> = [
      {
        key: 'kind',
        description: 'T_KIND_OF_ACTIVITIES',
        sortEnabled: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          return (
            <EllipsisDiv className="w-full flex-1" title={capitalize(data?.kind)}>
              {capitalize(data?.kind)}
            </EllipsisDiv>
          );
        },
        width: '15%',
        styles: { borderRight: 'none' }
      },
      // {
      //   key: 'options',
      //   description: '',
      //   sortEnabled: false,
      //   width: '4%',
      //   clickable: false,
      //   sortingInformation: {
      //     order: ''
      //   },
      //   render: (data: any) => {
      //     const { original } = data;
      //     return (
      //       <div className="hover:bg-[#d9d9d9]" style={{ borderRadius: '10px' }}>
      //         <OrganizationMenu
      //           className="px-0 py-0"
      //           iconSize="h-[28px] py-[4px] min-w-[20px]"
      //           organization={original}
      //         />
      //       </div>
      //     );
      //   },
      //   styles: {
      //     paddingRight: '0',
      //     paddingLeft: '0',
      //     borderLeft: 'none',
      //     rowStyle: { fontWeight: 'normal', padding: '0px' }
      //   }
      // },
      {
        key: 'options',
        description: '',
        sortEnabled: false,
        width: '7%',
        clickable: false,
        render: (data: any) => {
          const menuOptions: Array<any> = getMenuOptions(data);
          return menuOptions.length ? (
            <div className="px-0 py-0 hover:bg-[#d9d9d9]" style={{ borderRadius: '10px' }}>
              <MenuOptions options={menuOptions} folderData={data} />
            </div>
          ) : (
            <div />
          );
        },
        styles: {
          paddingRight: '0',
          paddingLeft: '0',
          borderLeft: 'none',
          rowStyle: { fontWeight: 'normal', padding: '0px', paddingLeft: '30px' }
        }
      },
      {
        key: 'status',
        description: 'T_STATUS',
        sortEnabled: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (row: any) => {
          return capitalize(row?.status);
        },
        width: '10%'
      },

      {
        key: 'performer-type',
        description: 'T_PERFORMER_TYPE',
        sortEnabled: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          const value = capitalize(data?.performerType);
          return (
            <EllipsisDiv className="w-full flex-1" title={value}>
              {value}
            </EllipsisDiv>
          );
        },
        width: '15%'
      },
      // {
      //   key: 'location',
      //   description: 'T_LOCATION',
      //   sortEnabled: false,
      //   clickable: true,
      //   sortingInformation: {
      //     order: ''
      //   },
      //   render: (data: any) => {
      //     return (
      //       <EllipsisDiv className="w-full flex-1" title={data?.locationId}>
      //         {data?.locationId}
      //       </EllipsisDiv>
      //     );
      //   },
      //   width: '15%'
      // },
      {
        key: 'doNotPerform',
        description: 'T_CARE_PLAN_ACTIVITY_PERFORMED_CHECK',
        sortEnabled: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          return !isUndefined(data?.doNotPerform)
            ? data?.doNotPerform
              ? t('T_FALSE')
              : t('T_TRUE')
            : '';
        },
        width: '15%'
      },
      // {
      //   key: 'description',
      //   description: 'T_DESCRIPTION',
      //   sortEnabled: false,
      //   clickable: true,
      //   sortingInformation: {
      //     order: ''
      //   },
      //   render: (data: any) => {
      //     return (
      //       <EllipsisDiv className="w-full flex-1" title={data?.description}>
      //         {data?.description}
      //       </EllipsisDiv>
      //     );
      //   },
      //   width: '30%'
      // },
      {
        key: 'schedule-period-start',
        description: 'T_START_DATE_TIME',
        sortEnabled: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          return data?.schedulePeriodStart
            ? moment(data.schedulePeriodStart).format(AppConstants.DEFAULT_DATE_FORMAT)
            : '';
        },
        width: '15%'
      },
      {
        key: 'schedule-period-end',
        description: 'T_END_DATE_TIME',
        sortEnabled: false,
        clickable: true,
        sortingInformation: {
          order: ''
        },
        render: (data: any) => {
          return data?.schedulePeriodEnd
            ? moment(data.schedulePeriodEnd).format(AppConstants.DEFAULT_DATE_FORMAT)
            : '';
        },
        width: '15%'
      },
      {
        key: 'updatedOn',
        description: 'T_LAST_MODIFIED_ON',
        sortEnabled: false,
        clickable: true,
        render: (data: any) => {
          return data.updatedOn
            ? moment(data.updatedOn).format(AppConstants.DEFAULT_DATE_FORMAT)
            : '';
        },
        width: '15%',
        sortingInformation: {
          order: ''
        }
      }
    ];

    return TABLE_HEADERS;
  };

  const [tableHeaders, setTableHeaders] = useState(tableHeadersBuilder());
  const [paramObj, setParamObj] = useState({
    activityStatus: '',
    activityKind: '',
    performerType: '',
    performerId: '',
    doNotPerform: false
  });

  const updateParamObj = (appliedFilters: any, appliedFilterList: any) => {
    const initialParamObj: any = { ...paramObj };
    const initialFilterList: any = [...filterList];
    initialFilterList.forEach((filterObj: any) => {
      switch (filterObj.id) {
        case 'performerType':
        case 'activityStatus':
        case 'activityKind':
          initialParamObj[filterObj.id] = map(appliedFilters[filterObj.id], 'key');
          break;
        case 'performerId':
          initialParamObj[filterObj.id] = map(appliedFilters[filterObj.id], 'userId');
          break;
      }
    });

    return initialParamObj;
  };

  const updateFilterListSelected = async (appliedFilters: any) => {
    const initialFilterList: any = [...filterList];
    const updatedFilterList = initialFilterList.map((filterObj: any) => {
      const selectedFilter = appliedFilters[filterObj.id] ? appliedFilters[filterObj.id] : [];
      return { ...filterObj, selectedFilter };
    });

    const performerTypeFilter = updatedFilterList.find(
      (filter: any) => filter.id === 'performerType'
    );

    const performerIdFilterIndex = updatedFilterList.findIndex(
      (filter: any) => filter.id === 'performerId'
    );
    const performerIdFilter =
      performerIdFilterIndex !== -1 ? updatedFilterList[performerIdFilterIndex] : {};
    if (performerTypeFilter?.selectedFilter?.length > 0 && !performerIdFilter?.itemsList?.length) {
      let [configItem] = performerTypeFilter?.selectedFilter;
      const { apiConfig, key } = configItem || {};
      const { fieldKey, fieldValues } = apiConfig || {};
      const listDataClone: any = cloneDeep(listData);
      if (key && !listDataClone?.[key]?.length) {
        const data = await getCareplanListData(apiConfig);
        if (data.length) {
          listDataClone[key] = data;
        }
      }
      performerIdFilter.itemsList = listDataClone?.[key];
      performerIdFilter.fieldKey = fieldKey;
      performerIdFilter.fieldValues = fieldValues;
      updatedFilterList[performerIdFilterIndex] = performerIdFilter;
      if (!isEqual(listData, listDataClone)) {
        dispatch(setListData(listDataClone));
      }
    }
    return updatedFilterList;
  };

  const getDisplayKey = (type: any) => {
    let key = type?.key;
    let element = find(
      careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_PERFORMER_TYPES],
      {
        key
      }
    );
    if (element) {
      let key = element?.apiConfig?.fieldValues;
      return key;
    } else {
      return [];
    }
  };

  const updatePillsData = (appliedFilters: any, appliedFilterList: any) => {
    const pillsData: any = [];

    if (appliedFilterList && appliedFilterList.length) {
      appliedFilterList.forEach((key: any) => {
        switch (key) {
          case 'activityStatus':
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({
                  name: item?.display || item?.value || '',
                  label: t('T_STATUS'),
                  id: key,
                  key: item.key
                  //hideIcon: queryParams.id ? true : false
                });
              });
            }

            break;

          case 'doNotPerform':
            if (appliedFilters[key]) {
              pillsData.push({
                name: t('T_TRUE'),
                label: t('T_CARE_PLAN_ACTIVITY_PERFORMED_CHECK'),
                id: key,
                key: key
                //hideIcon: queryParams.id ? true : false
              });
            }

            break;

          case 'activityKind':
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({
                  name: item?.display || item?.value || '',
                  label: t('T_CARE_PLAN_ACTIVITY_ACTIVITY_KIND_FILTER'),
                  id: key,
                  key: item.key
                  //hideIcon: queryParams.id ? true : false
                });
              });
            }

            break;

          case 'performerType':
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({
                  name: item?.display || item?.value || '',
                  label: t('T_CARE_PLAN_ACTIVITY_PERFORMER_TYPE_FILTER'),
                  id: key,
                  key: item.key
                  //hideIcon: queryParams.id ? true : false
                });
              });
            }

            break;

          case 'performerId':
            let fieldValues = getDisplayKey(appliedFilters?.performerType?.[0]) || [];
            if (appliedFilters[key]) {
              appliedFilters[key].forEach((item: any) => {
                pillsData.push({
                  name:
                    compact(fieldValues.map((fieldValue: string) => item[fieldValue])).join(' ') ||
                    item?.id,
                  label: t('T_CARE_PLAN_ACTIVITY_PERFORMER_FILTER'),
                  id: key,
                  key: item?.userId
                  //hideIcon: queryParams.id ? true : false
                });
              });
            }

            break;
        }
      });
    }

    return pillsData;
  };

  const initializeLoadNextData = (filters: any) => {
    const { performerType = [] } = filters || {};
    if (performerType.length && !loadNextData.searchPerformerType) {
      let [configItem] = performerType;
      const { apiConfig, key } = configItem || {};
      const { fieldKey, fieldValues } = apiConfig || {};
      setLoadNextData((prevState: any) => ({
        ...prevState,
        searchPerformerType: key,
        fieldValues
      }));
    }
  };

  const applyFilters = async (
    search: string = '',
    page: number,
    size: number,
    sortOrder: string = '',
    sortType: string = '',
    filters: any
  ) => {
    setIsLoading(true);
    const appliedFilterList = Object.keys(filters);
    setFilterPillsData(updatePillsData(filters, appliedFilterList));
    initializeLoadNextData(filters);
    setFilterList(await updateFilterListSelected(filters));
    setParamObj(updateParamObj(filters, appliedFilterList));
    loadCarePlanActivities(page, size, sortOrder, sortType, filters);
  };

  const refreshTableData = (
    search: string = '',
    page: number,
    size: number,
    sortOrder: string = '',
    sortType: string = '',
    filters: any
  ) => {
    setTableValues(page, size, search, sortOrder, sortType, filters);
  };

  const setTableValues = (
    page: number,
    size: number,
    search: string,
    sortOrder: string = '',
    sortType: string = '',
    filters: any
  ) => {
    setIsLoading(true);
  };

  const updateParamObjAfterRemovePills = (updatedFilterList: any) => {
    const initialParamObj: any = { ...paramObj };
    updatedFilterList.forEach((obj: any) => {
      if (obj.id === 'doNotPerform') {
        initialParamObj[obj.id] = false;
      } else {
        initialParamObj[obj.id] = obj.selectedFilter;
      }
    });
    setParamObj(initialParamObj);
    setIsLoading(true);
    const {
      searchText = '',
      currentPage: filterCurrentPage = AppConstants.DEFAULT_PAGE,
      size: filterSize = AppConstants.DEFAULT_PAGE_SIZE,
      sortName = '',
      sortType = ''
    } = globalFilters[tableKey] || {};

    dispatch(
      setTableOptions({
        [tableKey]: {
          searchText: searchText,
          currentPage: filterCurrentPage,
          size: filterSize,
          sortName: sortName,
          sortType: sortType,
          tableFilters: initialParamObj
        }
      })
    );
  };

  const updatePillsDataAfterRemovePills = (updatedFilterList: any) => {
    const pillsData: any = [];
    updatedFilterList.forEach((obj: any) => {
      switch (obj.id) {
        case 'activityStatus':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item.value,
              label: t('T_STATUS'),
              id: item.key,
              key: item.key
              //hideIcon: queryParams.id ? true : false
            });
          });

          break;

        case 'activityKind':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item?.display || item?.value || '',
              label: t('T_CARE_PLAN_ACTIVITY_ACTIVITY_KIND_FILTER'),
              id: item.key,
              key: item.key
              //hideIcon: queryParams.id ? true : false
            });
          });

          break;

        case 'performerType':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item?.display || item?.value || '',
              label: t('T_CARE_PLAN_ACTIVITY_PERFORMER_TYPE_FILTER'),
              id: item.key,
              key: item.key
              //hideIcon: queryParams.id ? true : false
            });
          });

          break;

        case 'doNotPerform':
          if (obj?.value) {
            pillsData.push({
              name: t('T_TRUE'),
              label: t('T_CARE_PLAN_ACTIVITY_PERFORMED_CHECK'),
              id: obj.key,
              key: obj.key
              //hideIcon: queryParams.id ? true : false
            });
          }
          break;

        case 'performerId':
          obj.selectedFilter.forEach((item: any) => {
            pillsData.push({
              name: item?.[loadNextData?.fieldValues?.[0]] || item?.id,
              label: t('T_CARE_PLAN_ACTIVITY_PERFORMER_FILTER'),
              id: item.key,
              key: item?.userId
              //hideIcon: queryParams.id ? true : false
            });
          });
      }
    });

    setFilterPillsData(pillsData);
  };

  const removeFromPillsFilter = (pillsFilterObj: any) => {
    let initialFilterList: any = [...filterList];
    const initialFilterObj = find(initialFilterList, { id: pillsFilterObj.id });

    if (initialFilterObj.id === 'performerType') {
      initialFilterObj.selectedFilter = [];
      let index = findIndex(filterList, function (item: any) {
        return item?.id === 'performerId';
      });
      if (index !== -1) {
        initialFilterList[index].selectedFilter = [];
        initialFilterList[index].itemsList = [];
      }
    }
    if (initialFilterObj.id === 'doNotPerform') {
      initialFilterObj.value = false;
    }

    if (initialFilterObj.id === 'performerId') {
      initialFilterObj.selectedFilter = [];
    }

    if (['activityStatus', 'activityKind'].includes(initialFilterObj.id)) {
      initialFilterObj.selectedFilter = filter(initialFilterObj.selectedFilter, (obj) => {
        return obj.key !== pillsFilterObj.key;
      });
    }
    const updatedFilterList = initialFilterList.map((obj: any) => {
      if (obj.id === pillsFilterObj.id) {
        return { ...obj, ...initialFilterObj };
      } else {
        return { ...obj };
      }
    });

    updateParamObjAfterRemovePills(updatedFilterList);
    updatePillsDataAfterRemovePills(updatedFilterList);
    setFilterList(updatedFilterList);
  };

  const elementSelector = (data: any) => {
    if (
      checkForPermission([
        (AppConstants.PERMISSION_CAREPLAN_TEMPLATE_ACTIVITY_GET,
        AppConstants.PERMISSION_CAREPLAN_TEMPLATE_ACTIVITY_PATCH)
      ])
    ) {
      const { id } = data;
      dispatch(setActivityData(data));
      navigate(
        RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES_VIEW.replace(':id', params?.id || '').replace(
          ':activityId',
          id
        )
      );
    } else {
      dispatch(
        setToastData({
          toastMessage: t('T_USER_NO_PERMISSION_TOAST_MESSAGE').replace(
            'Permission',
            t('T_CARE_PLAN_ACTIVITY')
          ),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const deleteCareplanActivityAPI = async () => {
    setIsLoading(true);
    let response = await deleteCareplanActivity(params?.id || careplanData?.id, deleteId);

    if ([HTTP_STATUS.HTTP_CREATED, HTTP_STATUS.HTTP_OK].includes(response.status)) {
      dispatch(
        setToastData({
          toastMessage: t('T_CARE_PLAN_ACTIVITY_DELETED_SUCCESS'),
          isToastActive: true,
          toastType: 'success'
        })
      );
      const { tableFilters, searchText, currentPage, size, sortName, sortType } =
        globalFilters[tableKey];
      loadCarePlanActivities(currentPage, size, sortName, sortType, tableFilters);
      setDeleteId('');
      setDeletePopup(false);
    } else {
      dispatch(
        setToastData({
          toastMessage: getFailureMessage(response),
          isToastActive: true,
          toastType: 'error'
        })
      );
      setDeleteId('');
      setIsLoading(false);
      setDeletePopup(false);
    }

    // } catch (e: any) {
    // } finally {
    //   setDeleteId('');
    //   setIsLoading(false);
    //   setDeletePopup(false);
    // }
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <TableContainer>
        <MenuButtonsPortal>
          <BiButton
            className={'max-w-1/2 mr-2 overflow-hidden whitespace-nowrap text-primary'}
            type="button"
            onClick={() => {
              navigate(RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_LIST);
            }}
          >
            {t('T_BACK')}
          </BiButton>
          {checkForPermission([AppConstants.PERMISSION_CAREPLAN_TEMPLATE_NOTES_CREATE]) && (
            <BiButton
              className={
                'flex flex-row  border border-solid border-primary-disabled py-1.5 text-base text-primary'
              }
              type="button"
              onClick={() => {
                navigate(
                  RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES_CREATE.replace(':id', params?.id || '')
                );
              }}
            >
              <div className="mr-2 h-5 w-5 self-center text-center">
                {customAddCircle(useTheme().theme.primaryAppColor)}
              </div>
              {t('T_ADD_AN_ACTIVITY')}
            </BiButton>
          )}
        </MenuButtonsPortal>

        <Modal
          showCloseIcon={true}
          width="48rem"
          headerText={t('T_DELETE_CONFIRMATION_TITLE')}
          toggleVisiblity={(value: boolean) => {
            setDeletePopup(value);
          }}
          isVisible={deletePopup}
          body={
            <div>
              <div>{t('T_CARE_PLAN_ACTIVITY_DELETE_PROMPT')}</div>
              <div className="mt-8 flex">
                <BiButton
                  className={'ml-auto mr-2 text-primary'}
                  type="button"
                  onClick={() => {
                    setDeletePopup(false);
                  }}
                >
                  {t('T_CANCEL')}
                </BiButton>
                <BiButton
                  className={'bg-primary text-white'}
                  type="button"
                  onClick={() => {
                    deleteCareplanActivityAPI();
                  }}
                >
                  {t('T_DELETE')}
                </BiButton>
              </div>
            </div>
          }
          headerLeftIcon={<ExclamationIcon className="h-7.5 w-6" />}
        />
        <TemplateTable
          tableData={rows}
          templateSelector={elementSelector}
          cellSelector={elementSelector}
          onRefreshTableData={refreshTableData}
          isSearchable={false}
          searchPlaceholder={t('T_SEARCH_NAME_CARE_PLAN_TITLE_TEMPLATE') || ''}
          tableHeaders={tableHeaders}
          setTableHeaders={setTableHeaders}
          setCurrentPage={(page: any) => {
            setCurrentActivePage(parseInt(page));
          }}
          currentPage={globalFilters?.[tableKey]?.currentPage}
          size={globalFilters?.[tableKey]?.size}
          pagingData={paging}
          containerClass="mt-0"
          showDeleteButton={true}
          theme={useTheme().theme}
          totalDataLength={rows.length}
          isPaginated
          isFilterable
          filtersList={filterList}
          applyFilters={applyFilters}
          pillsData={filterPillsData}
          removeFilter={removeFromPillsFilter}
          defaultPillsLabel={t('T_ACTIVITIES_FILTERS_DEFAULT_VIEW')}
          globalFilters={globalFilters}
          filterWidth={60}
          tablePage="careplan-activity-list"
        />
      </TableContainer>
    </LoaderContainer>
  );
};

export default CarePlanActivities;

const HeadingLabel = styled.label`
  font-family: Open Sans, sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #262626;
  margin: 0px 2px;
`;
